/**
 * Admin API fetch: session cookie + CSRF header.
 */
import { ensureCsrfCookie, withCsrfHeaders } from './csrf';

export function getAdminEmail(): string {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem('admin-email')?.trim() || '';
}

function isMutatingMethod(init?: RequestInit): boolean {
  const method = (init?.method ?? 'GET').toUpperCase();
  return method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
}

export async function adminFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (isMutatingMethod(init)) {
    await ensureCsrfCookie();
  }
  const res = await fetch(input, {
    ...init,
    headers: withCsrfHeaders(init),
    credentials: init?.credentials ?? 'include',
  });
  if (res.status === 401) {
    try {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      const isAuthLogin =
        typeof url === 'string'
          && (url.includes('/api/auth/admin/login') || url.includes('/api/auth/admin/forgot-password'));
      if (!isAuthLogin) {
        window.dispatchEvent(new Event('admin-session-expired'));
      }
    } catch {
      // ignore
    }
  }
  return res;
}

/** Install once while admin session is active so existing fetch() calls send cookie + CSRF. */
export function installAdminFetchInterceptor(): () => void {
  const original = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      if (typeof url === 'string' && (url.includes('/api/') || url.includes('/api?'))) {
        const isAuthLogin =
          url.includes('/api/auth/admin/login') || url.includes('/api/auth/admin/forgot-password');
        const send = () =>
          original(input, {
            ...init,
            headers: withCsrfHeaders(init),
            credentials: init?.credentials ?? 'include',
          }).then((res) => {
            if (res.status === 401 && !isAuthLogin) {
              window.dispatchEvent(new Event('admin-session-expired'));
            }
            return res;
          });
        if (isMutatingMethod(init)) {
          return ensureCsrfCookie().then(send);
        }
        return send();
      }
    } catch {
      // fall through
    }
    return original(input, init);
  }) as typeof window.fetch;
  return () => {
    window.fetch = original;
  };
}
